<template>
  <div v-if="showModal" class="consent-modal-overlay" @click.self="handleOverlayClick">
    <div class="consent-modal">
      <div class="consent-header">
        <h2 class="consent-title">
          {{ currentLang === 'kk' ? 'Құжаттармен келісім' : 'Согласие с документами' }}
        </h2>
      </div>

      <div class="consent-body">
        <p class="consent-intro">
          {{ currentLang === 'kk' 
            ? 'Жүйеде жұмысты жалғастыру үшін келесі құжаттармен танысып, келісуіңіз керек:' 
            : 'Для продолжения работы в системе необходимо ознакомиться и согласиться с документами:' 
          }}
        </p>

        <div class="consent-checkboxes">
          <!-- Политика конфиденциальности -->
          <div class="consent-checkbox-item">
            <input
              type="checkbox"
              id="privacyPolicy"
              v-model="privacyPolicyChecked"
              class="consent-checkbox"
            />
            <label for="privacyPolicy" class="consent-label">
              <span class="checkbox-text">
                {{ currentLang === 'kk' ? 'Мен' : 'Я прочитал(а) и согласен(а) с' }}
              </span>
              <a
                :href="privacyPolicyLink"
                target="_blank"
                rel="noopener noreferrer"
                class="consent-link"
                @click.stop
              >
                {{ currentLang === 'kk' 
                  ? 'Құпиялылық саясатымен' 
                  : 'Политикой конфиденциальности' 
                }}
              </a>
            </label>
          </div>

          <!-- Пользовательское соглашение -->
          <div class="consent-checkbox-item">
            <input
              type="checkbox"
              id="termsOfService"
              v-model="termsOfServiceChecked"
              class="consent-checkbox"
            />
            <label for="termsOfService" class="consent-label">
              <span class="checkbox-text">
                {{ currentLang === 'kk' ? 'Мен' : 'Я прочитал(а) и согласен(а) с' }}
              </span>
              <a
                :href="termsOfServiceLink"
                target="_blank"
                rel="noopener noreferrer"
                class="consent-link"
                @click.stop
              >
                {{ currentLang === 'kk' 
                  ? 'Пайдаланушы келісімімен' 
                  : 'Пользовательским соглашением' 
                }}
              </a>
            </label>
          </div>
        </div>
        <p class="consent-intro">
          {{ currentLang === 'kk' 
            ? 'Жеке тұлғалар туралы дербес деректерді қамтитын электрондық ақпараттық ресурстарды оларға мүліктік және (немесе) моральдық залал келтіру, Қазақстан Республикасының заңдарында кепілдік берілген құқықтар мен бостандықтардың жүзеге асырылуын шектеу мақсатында пайдалану, сондай-ақ дербес деректерді қорғау жөніндегі шараларды сақтамау Қазақстан Республикасының Әкімшілік құқық бұзушылық туралы кодексінің 641-бабына, C147-бабына сәйкес жауаптылыққа әкеп соғады.' 
            : 'Использование электронных информационных ресурсов, содержащих персональные данные о физических лицах, в целях причинения им имущественного и (или) морального вреда, ограничения реализации прав и свобод, гарантированных законами Республики Казахстан, а также несоблюдение мер по защите персональных данных влечет ответственность в соответствии со статьями 641 Кодекса Республики Казахстан об административных правонарушениях, 147 Уголовного кодекса Республики Казахстан' 
          }}
        </p>

        <!-- Сообщение об ошибке -->
        <div v-if="errorMessage" class="consent-error">
          {{ errorMessage }}
        </div>
      </div>

      <div class="consent-footer">
        <button
          @click="handleAccept"
          :disabled="!canAccept || isLoading"
          class="consent-button consent-button-accept"
          :class="{ 'button-disabled': !canAccept || isLoading }"
        >
          <span v-if="isLoading" class="loading-spinner"></span>
          <span v-else>{{ currentLang === 'kk' ? 'Қабылдау' : 'Принять' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import api from '../services/api';

export default {
  name: 'ConsentModal',
  setup() {
    const showModal = ref(false);
    const privacyPolicyChecked = ref(false);
    const termsOfServiceChecked = ref(false);
    const isLoading = ref(false);
    const errorMessage = ref('');

    // Получаем текущий язык из localStorage или i18n
    const currentLang = computed(() => {
      // Проверяем localStorage
      const storedLang = localStorage.getItem('language');
      if (storedLang) return storedLang;
      
      // Проверяем HTML атрибут lang
      const htmlLang = document.documentElement.lang;
      if (htmlLang) return htmlLang;
      
      // По умолчанию русский
      return 'ru';
    });

    // Ссылки на документы в зависимости от языка
    const privacyPolicyLink = computed(() => {
      return currentLang.value === 'kk' 
        ? '/Политика_конфиденциальности_kk.pdf' 
        : '/Политика_конфиденциальности_ru.pdf';
    });

    const termsOfServiceLink = computed(() => {
      return currentLang.value === 'kk'
        ? '/Пользовательское соглашение_kk.pdf'
        : '/Пользовательское соглашение_ru.pdf';
    });

    // Можно ли нажать кнопку "Принять"
    const canAccept = computed(() => {
      return privacyPolicyChecked.value && termsOfServiceChecked.value;
    });

    // Проверка согласия при монтировании компонента
    const checkConsent = async () => {
      try {
        const response = await api.get('/users/check-consent/');

        // Если пользователь еще не дал согласие, показываем модалку
        if (!response.data.has_consented) {
          showModal.value = true;
        }
      } catch (error) {
        console.error('Ошибка при проверке согласия:', error);
        // Если ошибка - показываем модалку для безопасности
        showModal.value = true;
      }
    };

    // Обработка принятия согласия
    const handleAccept = async () => {
      if (!canAccept.value || isLoading.value) {
        return;
      }

      isLoading.value = true;
      errorMessage.value = '';

      try {
        await api.post('/users/save-consent/', {
          privacy_policy: privacyPolicyChecked.value,
          terms_of_service: termsOfServiceChecked.value,
          language: currentLang.value
        });

        // Успешно сохранено - закрываем модалку
        showModal.value = false;
      } catch (error) {
        console.error('Ошибка при сохранении согласия:', error);
        errorMessage.value = currentLang.value === 'kk'
          ? 'Қате пайда болды. Қайталап көріңіз.'
          : 'Произошла ошибка. Попробуйте еще раз.';
      } finally {
        isLoading.value = false;
      }
    };

    // Обработка клика по overlay (блокируем закрытие)
    const handleOverlayClick = () => {
      // Модалку нельзя закрыть кликом по overlay
      // Пользователь должен принять согласие
    };

    onMounted(() => {
      checkConsent();
    });

    return {
      showModal,
      privacyPolicyChecked,
      termsOfServiceChecked,
      isLoading,
      errorMessage,
      currentLang,
      privacyPolicyLink,
      termsOfServiceLink,
      canAccept,
      handleAccept,
      handleOverlayClick
    };
  }
};
</script>

<style scoped>
.consent-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.consent-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.consent-header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.consent-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
}

.consent-body {
  padding: 24px;
}

.consent-intro {
  margin: 0 0 24px;
  font-size: 15px;
  color: #4b5563;
  line-height: 1.6;
}

.consent-checkboxes {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 16px;
}

.consent-checkbox-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.consent-checkbox {
  width: 20px;
  height: 20px;
  margin-top: 2px;
  cursor: pointer;
  flex-shrink: 0;
}

.consent-label {
  flex: 1;
  font-size: 15px;
  color: #374151;
  line-height: 1.5;
  cursor: pointer;
  user-select: none;
}

.checkbox-text {
  margin-right: 4px;
}

.consent-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s;
}

.consent-link:hover {
  border-bottom-color: #3b82f6;
}

.consent-error {
  margin-top: 16px;
  padding: 12px;
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 14px;
}

.consent-footer {
  padding: 16px 24px 24px;
  display: flex;
  justify-content: flex-end;
}

.consent-button {
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.consent-button-accept {
  background-color: #3b82f6;
  color: white;
}

.consent-button-accept:hover:not(.button-disabled) {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.button-disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spinner-rotation 0.8s linear infinite;
}

@keyframes spinner-rotation {
  to {
    transform: rotate(360deg);
  }
}

/* Адаптивность для мобильных устройств */
@media (max-width: 640px) {
  .consent-modal {
    margin: 10px;
    max-height: 95vh;
  }

  .consent-header {
    padding: 20px 20px 12px;
  }

  .consent-title {
    font-size: 20px;
  }

  .consent-body {
    padding: 20px;
  }

  .consent-footer {
    padding: 12px 20px 20px;
  }

  .consent-button {
    width: 100%;
  }
}
</style>

